import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

import { firebaseService } from './FirebaseService';
import { User, SpaceThing, Todo, CalendarEvent, FavoriteItem, ThemeType, ThemeConfig } from './types';

import LoginScreen from './components/LoginScreen';
import Header from './components/Header';
import Navigation from './components/Navigation';
import MySpaceDashboard from './components/MySpaceDashboard';
import ThingsInSpace from './components/ThingsInSpace';
import Favorites from './components/Favorites';
import CalendarView from './components/CalendarView';
import TodoList from './components/TodoList';
import Chat from './components/Chat';
import Question from './components/Question';
import ValentineBackground from './components/ValentineBackground';
import ValentineMessage from './components/ValentineMessage';

const themes: Record<ThemeType, ThemeConfig> = {
  matcha: {
    primary: 'bg-green-100',
    secondary: 'bg-green-50',
    accent: 'bg-green-600',
    accentHover: 'hover:bg-green-700',
    text: 'text-green-900',
    textLight: 'text-green-700',
    border: 'border-green-300',
    button: 'bg-green-600 hover:bg-green-700',
    cardBg: 'bg-white/80',
    shadow: 'shadow-green-200'
  },
  purple: {
    primary: 'bg-purple-100',
    secondary: 'bg-purple-50',
    accent: 'bg-purple-600',
    accentHover: 'hover:bg-purple-700',
    text: 'text-purple-900',
    textLight: 'text-purple-700',
    border: 'border-purple-300',
    button: 'bg-purple-600 hover:bg-purple-700',
    cardBg: 'bg-white/80',
    shadow: 'shadow-purple-200'
  },
  valentine: {
    primary: 'bg-pink-200',
    secondary: 'bg-pink-100',
    accent: 'bg-pink-600',
    accentHover: 'hover:bg-pink-700',
    text: 'text-pink-900',
    textLight: 'text-pink-700',
    border: 'border-pink-300',
    button: 'bg-pink-600 hover:bg-pink-700',
    cardBg: 'bg-white/80',
    shadow: 'shadow-pink-200'
  }
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<ThemeType>('matcha');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const [spaceThings, setSpaceThings] = useState<SpaceThing[]>([]);
  const [favorites, setFavorites] = useState<FavoriteItem[]>([
    { id: 1, name: 'Bunny', imageUrl: null, localImageUrl: '/images/bunny.jpg', category: 'bunny', useUnsplash: false },
    { id: 2, name: 'Capybara', imageUrl: null, localImageUrl: '/images/capybara.jpg', category: 'capybara', useUnsplash: false },
    { id: 3, name: 'Maggi', imageUrl: null, localImageUrl: '/images/maggi.jpg', category: 'maggi noodles', useUnsplash: false },
    { id: 4, name: 'Matcha', imageUrl: null, localImageUrl: '/images/matcha.jpg', category: 'matcha latte', useUnsplash: false },
    { id: 5, name: 'Cat', imageUrl: null, localImageUrl: '/images/cat.jpg', category: 'cat', useUnsplash: false },
    { id: 6, name: 'Puppy', imageUrl: null, localImageUrl: '/images/puppy.jpg', category: 'puppy golden retriever', useUnsplash: false },
    { id: 7, name: 'Lando Norris', imageUrl: null, localImageUrl: '/images/lando.jpg', category: 'Lando Norris', useUnsplash: false },
    { id: 8, name: 'Charles Leclerc', imageUrl: null, localImageUrl: '/images/charles.jpg', category: 'Charles Leclerc', useUnsplash: false },
    { id: 9, name: 'Conrad', imageUrl: null, localImageUrl: '/images/conrad.jpg', category: 'conrad fisher', useUnsplash: false },
    { id: 10, name: 'Loki', imageUrl: null, localImageUrl: '/images/loki.jpg', category: 'tom hiddleston loki', useUnsplash: false },
    { id: 11, name: 'Ironman', imageUrl: null, localImageUrl: '/images/ironman.jpg', category: 'iron man tony stark', useUnsplash: false },
    { id: 12, name: 'Upper East Side', imageUrl: null, localImageUrl: '/images/ues.jpg', category: 'upper east side nyc', useUnsplash: false },
    { id: 13, name: 'Twilight', imageUrl: null, localImageUrl: '/images/twilight.jpg', category: 'twilight movie', useUnsplash: false }
  ]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const currentTheme = themes[theme];

  const handleGoogleSignIn = async () => {
    // Just trigger sign-in; onAuthStateChanged will handle whitelist check
    await firebaseService.signInWithGoogle();
  };

  const handleSignOut = async () => {
    await firebaseService.signOut();
    setUser(null);
  };

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'matcha') return 'purple';
      if (prev === 'purple') return 'valentine';
      return 'matcha';
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseService.auth, async (firebaseUser) => {
      
      if (firebaseUser && firebaseUser.email) {
        try {
          const isWhitelisted = await firebaseService.isUserWhitelisted(firebaseUser.email);
          
          if (isWhitelisted) {
            setUser({
              name: firebaseUser.displayName || 'User',
              email: firebaseUser.email,
              uid: firebaseUser.uid
            });
          } else {
            alert("Sorry, you are not authorized to access this application.");
            await firebaseService.signOut();
            setUser(null);
          }
        } catch (error: any) {
          console.error("[Auth] ERROR during authentication:", {
            message: error?.message,
            code: error?.code,
            name: error?.name,
            stack: error?.stack
          });
          
          // If it's a network error, allow the user in anyway (for development)
          if (error?.code === 'unavailable' || error?.message?.includes('offline')) {
            console.warn("[Auth] Network unavailable, allowing access for development");
            setUser({
              name: firebaseUser.displayName || 'User',
              email: firebaseUser.email,
              uid: firebaseUser.uid
            });
          } else {
            console.error("[Auth] Non-network error, denying access");
            setUser(null);
          }
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    if (user) {
      firebaseService.getTodos().then(setTodos);
      firebaseService.getEvents().then(setEvents);
      firebaseService.getSpaceThings().then(setSpaceThings);
    }
  }, [user]);

  if (loading) {
    return (
      <div className={`min-h-screen ${currentTheme.primary} flex items-center justify-center`}>
        <p className={`${currentTheme.text}`}>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen theme={currentTheme} onSignIn={handleGoogleSignIn} />;
  }

  return (
    <div className={`min-h-screen ${currentTheme.secondary} relative`}>
      {/* Valentine's theme background and message */}
      {theme === 'valentine' && (
        <>
          <ValentineBackground />
          <ValentineMessage />
        </>
      )}
      
      <div className="relative z-10">
        <Header 
          theme={currentTheme} 
          onToggleTheme={toggleTheme} 
          onSignOut={handleSignOut}
        />
        
        <Navigation theme={currentTheme} />

        <main className="container mx-auto px-4 py-6 md:py-8">
          <Routes>
            <Route path="/" element={
              <MySpaceDashboard 
                theme={currentTheme} 
                spaceThings={spaceThings}
                favorites={favorites}
                todos={todos}
                events={events}
              />
            } />
            <Route path="/things" element={
              <ThingsInSpace 
                theme={currentTheme} 
                things={spaceThings}
                setThings={setSpaceThings}
              />
            } />
            <Route path="/favorites" element={
              <Favorites 
                theme={currentTheme} 
                favorites={favorites}
                setFavorites={setFavorites}
              />
            } />
            <Route path="/calendar" element={
              <CalendarView 
                theme={currentTheme} 
                events={events}
                setEvents={setEvents}
              />
            } />
            <Route path="/todos" element={
              <TodoList 
                theme={currentTheme} 
                todos={todos}
                setTodos={setTodos}
              />
            } />
            <Route path="/chat" element={<Chat theme={currentTheme} />} />
            <Route path="/question" element={<Question theme={currentTheme} />} />
          </Routes>
        </main>

        <style>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default App;